import { forwardRef, ReactNode, CSSProperties } from 'react'
import { useTranslation } from 'next-i18next'

import { TimesTableCell, TimesTableCellStatus } from '~/bootcamp/types/bootcamp.types'
import Times from '~/bootcamp/Activities/components/Times'
import Input from '~/bootcamp/Activities/components/Input'

import styles from './TimesTable4x4.module.css'

interface InputProps {
  onEnterPress?: () => void // Optional Enter key press handler
  onXchange: (v: string) => void // X input field change handler
  onYchange: (v: string) => void // Y input field change handler
  xInput?: string // X input field value
  yInput?: string // Y input field value
  cell: TimesTableCell // Cell data
  status: TimesTableCellStatus // Cell status
}

// eslint-disable-next-line react/display-name
const TableCell = forwardRef<HTMLInputElement | null, InputProps>((props, ref) => {
  const { xInput, yInput, status, cell, onXchange, onYchange, onEnterPress } = props
  const { t } = useTranslation('generics')

  switch (status) {
    case TimesTableCellStatus.Wrong:
      return (
        <Cell className="border border-theory-red">
          <div className="flex items-center justify-between font-semibold">
            <span className="text-theory-red">{xInput}</span>{' '}
            <span className="text-purple">
              <Times />
            </span>{' '}
            <span className="text-theory-red">{yInput}</span>
          </div>
        </Cell>
      )

    case TimesTableCellStatus.Completed:
      return (
        <Cell className="border border-green-status">
          <span className="font-semibold text-green-status">{cell.x * cell.y}</span>
        </Cell>
      )

    case TimesTableCellStatus.Selected:
      return (
        <Cell className="shadow-purple">
          <div className="flex items-center justify-between font-semibold">
            <Input
              aria-label={t('ariaLabelButtons.insertFactor')}
              isSmall
              key={`${cell.x}-${cell.y}-x`}
              maxDigits={1}
              onChange={onXchange}
              onEnter={onEnterPress ? onEnterPress : undefined}
              ref={ref}
              type="number"
              value={xInput?.toString() || ''}
            />
            <span className="text-purple -mx-1">
              <Times />
            </span>
            <Input
              aria-label={t('ariaLabelButtons.insertFactor')}
              isSmall
              key={`${cell.x}-${cell.y}-y`}
              maxDigits={1}
              onChange={onYchange}
              onEnter={onEnterPress ? onEnterPress : undefined}
              type="number"
              value={yInput?.toString() || ''}
            />
          </div>
        </Cell>
      )

    default:
      return <Cell />
  }
})

interface CellProps {
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

const Cell = ({ children, className, style }: CellProps): React.ReactElement => {
  return (
    <div
      className={`${styles['times-table-cell-width']} ${
        styles['times-table-cell-height']
      } bg-white text-center flex justify-center items-center shadow-card ${className || ''}`}
      style={style}
    >
      {children}
    </div>
  )
}

export default TableCell
